import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Input, Loading, Modal } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import DownloadQualityOptions from '@/components/Files/DownloadQualityOptions.tsx'
import DownloadProgressBar from '@/components/Files/DownloadProgressBar.tsx'
import useDownloadProgress from '@/hooks/useDownloadProgress.ts'
import { ALL_DOWNLOAD_QUALITIES, DownloadQuality } from '@/models/downloadOptions.ts'
import { IconArrowLeft, IconCopy, IconDownload, IconFileExport, IconMail, IconTrash, IconUser, IconX } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { ProfProject } from '@/models/profProject.ts'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import { toast } from 'react-toastify'
import { AlbumFile } from '@/models/albumFile.ts'

type SeparatorMode = ',' | ';' | 'newline' | 'custom'

const SEPARATOR_PRESETS: Record<Exclude<SeparatorMode, 'custom'>, string> = {
  ',': ', ',
  ';': '; ',
  newline: '\n',
}

function copyToClipboard(text: string, successMsg = 'Kopiert') {
  navigator.clipboard.writeText(text).then(() => {
    toast(successMsg, { closeButton: true, autoClose: 2000 })
  }).catch(() => {
    toast.error('Kopieren fehlgeschlagen')
  })
}

export default function SelectionDetailPage() {
  const { projectId, selectionId } = useParams<{ projectId: string, selectionId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const {
    getProject,
    getSelection,
    getSelectionAlbums,
    getSelectionAlbumFiles,
    deleteSelection,
    removeSelectionAlbum,
    downloadSelection,
    getMultiDownload,
  } = useClient()

  const [isDeletingSelection, setIsDeletingSelection] = useState(false)
  const deleteSelectionModalRef = useRef<HTMLDialogElement>(null)

  // Download state
  const downloadModalRef = useRef<HTMLDialogElement>(null)
  const [downloadStep, setDownloadStep] = useState<'quality' | 'processing' | 'done' | 'error'>('quality')
  const [downloadError, setDownloadError] = useState<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const { progress: downloadProgress, reportProgress, resetProgress } = useDownloadProgress()

  // Export state — persisted in localStorage so settings carry over to next visit
  const exportModalRef = useRef<HTMLDialogElement>(null)
  const [exportPrefix, setExportPrefix] = useState(() => localStorage.getItem('selectionExport.prefix') ?? '')
  const [separatorMode, setSeparatorMode] = useState<SeparatorMode>(() => {
    const stored = localStorage.getItem('selectionExport.separatorMode')
    return (stored === ',' || stored === ';' || stored === 'newline' || stored === 'custom') ? stored : 'newline'
  })
  const [customSeparator, setCustomSeparator] = useState(() => localStorage.getItem('selectionExport.customSeparator') ?? '')
  const [exportFilenames, setExportFilenames] = useState<string[]>([])
  const [isLoadingExport, setIsLoadingExport] = useState(false)

  useEffect(() => { localStorage.setItem('selectionExport.prefix', exportPrefix) }, [exportPrefix])
  useEffect(() => { localStorage.setItem('selectionExport.separatorMode', separatorMode) }, [separatorMode])
  useEffect(() => { localStorage.setItem('selectionExport.customSeparator', customSeparator) }, [customSeparator])

  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
  }, [])

  const projectQuery = useQuery({
    queryKey: ['project', projectId],
    queryFn: async () => {
      const res = await getProject(projectId!)
      return res.data as ProfProject
    },
    enabled: !!projectId,
  })

  const selectionQuery = useQuery({
    queryKey: ['selection', selectionId],
    queryFn: async () => {
      const res = await getSelection(selectionId!)
      return res.data
    },
    enabled: !!selectionId,
  })

  const albumsQuery = useQuery({
    queryKey: ['selection', selectionId, 'albums'],
    queryFn: async () => {
      const res = await getSelectionAlbums(selectionId!)
      return res.data
    },
    enabled: !!selectionId,
  })

  if (projectQuery.isLoading || selectionQuery.isLoading || albumsQuery.isLoading) {
    return <LoadBig />
  }

  if (projectQuery.error || selectionQuery.error || albumsQuery.error || !selectionQuery.data || !projectQuery.data) {
    return (
      <div className='container mx-auto p-4'>
        <p className='text-error'>Fehler beim Laden der Auswahl</p>
      </div>
    )
  }

  const project = projectQuery.data
  const selection = selectionQuery.data
  const albums = albumsQuery.data || []
  const driveId = project.drive.id
  const totalFiles = albums.reduce((sum, a) => sum + (a.files_count || 0), 0)

  const createdAt = new Date(selection.created_at).toLocaleString('de-DE', {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })

  async function handleDeleteSelection() {
    setIsDeletingSelection(true)
    try {
      await deleteSelection(driveId, selectionId!)
      queryClient.invalidateQueries({ queryKey: ['drive', driveId, 'selections'] })
      toast('Auswahl gelöscht', { autoClose: 2000 })
      navigate(`/portal/projects/${projectId}?tab=selections`)
    } catch {
      toast.error('Fehler beim Löschen der Auswahl')
    } finally {
      setIsDeletingSelection(false)
    }
  }

  async function handleRemoveAlbum(selectionAlbumId: string) {
    if (!confirm('Album aus der Auswahl entfernen?')) return
    try {
      await removeSelectionAlbum(driveId, selectionId!, selectionAlbumId)
      queryClient.invalidateQueries({ queryKey: ['selection', selectionId, 'albums'] })
    } catch {
      toast.error('Fehler beim Entfernen des Albums')
    }
  }

  function openDownloadModal() {
    setDownloadError(null)
    setDownloadStep('quality')
    downloadModalRef.current?.showModal()
  }

  function startDownload(quality: DownloadQuality) {
    setDownloadError(null)
    setDownloadStep('processing')
    resetProgress()
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)

    downloadSelection(selectionId!, quality).then(res => {
      const requestId = res.data.id
      pollIntervalRef.current = setInterval(async () => {
        try {
          const statusRes = await getMultiDownload(driveId, requestId)
          const data = statusRes.data
          if (data.status !== 'failed') {
            reportProgress(data.progress)
          }
          if (data.status === 'done') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
            if (data.download_url) {
              const link = document.createElement('a')
              link.href = data.download_url
              document.body.appendChild(link)
              link.click()
              document.body.removeChild(link)
            }
            setDownloadStep('done')
            downloadModalRef.current?.close()
          } else if (data.status === 'failed') {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
            setDownloadError(data.error_message || 'Download fehlgeschlagen')
            setDownloadStep('error')
          }
        } catch {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
          setDownloadError('Download fehlgeschlagen')
          setDownloadStep('error')
        }
      }, 1000)
    }).catch(() => {
      setDownloadError('Download konnte nicht gestartet werden')
      setDownloadStep('error')
    })
  }

  async function openExportModal() {
    setIsLoadingExport(true)
    exportModalRef.current?.showModal()
    try {
      const responses = await Promise.all(
        albums.map(album => getSelectionAlbumFiles(selectionId!, album.id))
      )
      const names: string[] = []
      for (const res of responses) {
        const files: AlbumFile[] = res.data?.results || res.data || []
        for (const f of files) {
          if (f.file_name) names.push(f.file_name)
        }
      }
      setExportFilenames(names)
    } catch {
      toast.error('Fehler beim Laden der Dateinamen')
      setExportFilenames([])
    } finally {
      setIsLoadingExport(false)
    }
  }

  const effectiveSeparator = separatorMode === 'custom' ? customSeparator : SEPARATOR_PRESETS[separatorMode]
  const exportOutput = exportFilenames.map(name => exportPrefix + name).join(effectiveSeparator)

  return (
    <div className='container mx-auto px-3 md:px-5 py-5'>
      <Link
        to={`/portal/projects/${projectId}?tab=selections`}
        className='inline-flex items-center gap-2 text-sm opacity-50 hover:opacity-100 transition-opacity mb-6'
      >
        <IconArrowLeft size={16} />
        Auswahlen
      </Link>

      <div className='bg-neutral p-4 md:p-6 border border-[var(--portal-border)] mb-5'>
        <div className='flex flex-row items-start justify-between gap-4 mb-4 flex-wrap'>
          <div className='flex-1 min-w-0'>
            <h1 className='font-serif text-2xl md:text-3xl mb-1'>
              {selection.title || 'Auswahl'}
            </h1>
            <p className='text-sm opacity-50'>
              {createdAt} · {totalFiles} {totalFiles === 1 ? 'Bild' : 'Bilder'} aus {albums.length} {albums.length === 1 ? 'Album' : 'Alben'}
            </p>
          </div>
          <div className='flex flex-row gap-2 flex-wrap'>
            <Button
              color='accent'
              variant='outline'
              size='sm'
              onClick={openExportModal}
              disabled={totalFiles === 0}
            >
              <IconFileExport size={16} />
              Exportieren
            </Button>
            <Button
              color='accent'
              size='sm'
              onClick={openDownloadModal}
              disabled={totalFiles === 0 || downloadStep === 'processing'}
            >
              <IconDownload size={16} />
              Herunterladen
            </Button>
            <Button
              color='error'
              variant='outline'
              size='sm'
              onClick={() => deleteSelectionModalRef.current?.showModal()}
            >
              <IconTrash size={16} />
              Auswahl löschen
            </Button>
          </div>
        </div>
        <div className='flex flex-col gap-3'>
          {selection.name && (
            <div className='flex items-start gap-2'>
              <IconUser size={18} className='opacity-50 mt-0.5' />
              <div>
                <p className='text-xs uppercase tracking-wider opacity-60 font-semibold mb-1'>Name</p>
                <p>{selection.name}</p>
              </div>
            </div>
          )}
          <div className='flex items-start gap-2'>
            <IconMail size={18} className='opacity-50 mt-0.5' />
            <div>
              <p className='text-xs uppercase tracking-wider opacity-60 font-semibold mb-1'>Email</p>
              <p>{selection.email || '—'}</p>
            </div>
          </div>
          {selection.message && (
            <div>
              <p className='text-xs uppercase tracking-wider opacity-60 font-semibold mb-1'>Nachricht</p>
              <p className='whitespace-pre-wrap'>{selection.message}</p>
            </div>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-5'>
        {albums.length === 0 && (
          <div className='bg-neutral p-4 border border-[var(--portal-border)] text-center opacity-60'>
            Keine Alben in dieser Auswahl.
          </div>
        )}
        {albums.map(album => (
          <SelectionAlbumSection
            key={album.id}
            driveId={driveId}
            selectionId={selectionId!}
            selectionAlbumId={album.id}
            title={album.title}
            filesCount={album.files_count}
            includeAllFiles={album.include_all_files}
            onRemoveAlbum={() => handleRemoveAlbum(album.id)}
          />
        ))}
      </div>

      <Modal ref={deleteSelectionModalRef} className='bg-neutral'>
        <Modal.Header>
          <div className='flex flex-row gap-2 justify-center'>
            <div className='bg-primary text-neutral rounded-full p-1'>
              <IconTrash size={20} />
            </div>
            Auswahl löschen
          </div>
          <form method='dialog'>
            <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5' disabled={isDeletingSelection}>✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className='flex flex-col gap-3 items-center'>
            <p className='text-center'>
              Die Auswahl wird endgültig gelöscht.
            </p>
            <Button
              color='error'
              onClick={handleDeleteSelection}
              loading={isDeletingSelection}
              disabled={isDeletingSelection}
            >
              Löschen
            </Button>
            <form method='dialog'>
              <Button color='ghost' disabled={isDeletingSelection}>Abbrechen</Button>
            </form>
          </div>
        </Modal.Body>
      </Modal>

      <Modal ref={downloadModalRef} className='bg-neutral'>
        <Modal.Header>
          <div className='flex flex-row gap-2 justify-center'>
            <div className='bg-primary text-neutral rounded-full p-1'>
              <IconDownload size={20} />
            </div>
            Download
          </div>
          <form method='dialog'>
            <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5' disabled={downloadStep === 'processing'}>✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          {downloadStep === 'quality' && (
            <div className='flex flex-col gap-3 items-center'>
              {/* The photographer's own portal view is never restricted by the drive's
                  download_options — that setting only governs what guests are offered. */}
              <DownloadQualityOptions
                qualities={ALL_DOWNLOAD_QUALITIES}
                onSelect={startDownload}
                fullWidth
              />
              <form method='dialog'>
                <Button color='ghost' className='mt-2'>Abbrechen</Button>
              </form>
            </div>
          )}
          {downloadStep === 'processing' && (
            <div className='flex flex-col gap-3 items-center py-4'>
              <DownloadProgressBar progress={downloadProgress} label='Download wird erstellt' />
              <p className='text-center text-sm opacity-60'>
                Bei vielen oder großen Dateien kann das einen Moment dauern.
              </p>
            </div>
          )}
          {downloadStep === 'error' && (
            <div className='flex flex-col gap-3 items-center py-4'>
              <p className='text-center text-error'>{downloadError}</p>
              <form method='dialog'>
                <Button color='ghost'>Schließen</Button>
              </form>
            </div>
          )}
          {downloadStep === 'done' && (
            <div className='flex flex-col gap-3 items-center py-4'>
              <p className='text-center'>Download erstellt.</p>
              <form method='dialog'>
                <Button color='ghost'>Schließen</Button>
              </form>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal ref={exportModalRef} className='bg-neutral max-w-2xl'>
        <Modal.Header>
          <div className='flex flex-row gap-2 justify-center'>
            <div className='bg-primary text-neutral rounded-full p-1'>
              <IconFileExport size={20} />
            </div>
            Dateinamen exportieren
          </div>
          <form method='dialog'>
            <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5'>✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className='flex flex-col gap-4'>
            <label className='form-control w-full'>
              <div className='label'>
                <span className='label-text'>Präfix (vor jedem Dateinamen)</span>
              </div>
              <Input
                color='primary'
                placeholder='z.B. /pfad/zur/datei/'
                value={exportPrefix}
                onChange={e => setExportPrefix(e.target.value)}
              />
            </label>

            <div className='form-control w-full'>
              <div className='label'>
                <span className='label-text'>Trennzeichen</span>
              </div>
              <div className='flex flex-row gap-3 flex-wrap items-center'>
                {(['newline', ',', ';', 'custom'] as SeparatorMode[]).map(mode => (
                  <label key={mode} className='flex items-center gap-2 cursor-pointer'>
                    <input
                      type='radio'
                      name='separator'
                      className='radio radio-primary radio-sm'
                      checked={separatorMode === mode}
                      onChange={() => setSeparatorMode(mode)}
                    />
                    <span className='text-sm'>
                      {mode === 'newline' && 'Neue Zeile'}
                      {mode === ',' && 'Komma ( , )'}
                      {mode === ';' && 'Semikolon ( ; )'}
                      {mode === 'custom' && 'Eigenes'}
                    </span>
                  </label>
                ))}
              </div>
              {separatorMode === 'custom' && (
                <textarea
                  className='textarea textarea-bordered textarea-primary w-full mt-2 min-h-16 font-mono text-sm'
                  placeholder='z.B. | oder Zeilenumbruch'
                  value={customSeparator}
                  onChange={e => setCustomSeparator(e.target.value)}
                />
              )}
            </div>

            <label className='form-control w-full'>
              <div className='label'>
                <span className='label-text'>
                  Ergebnis ({exportFilenames.length} {exportFilenames.length === 1 ? 'Datei' : 'Dateien'})
                </span>
              </div>
              {isLoadingExport ? (
                <div className='flex justify-center py-4'>
                  <Loading color='primary' />
                </div>
              ) : (
                <textarea
                  className='textarea textarea-bordered w-full min-h-40 font-mono text-sm'
                  value={exportOutput}
                  readOnly
                  onFocus={e => e.currentTarget.select()}
                />
              )}
            </label>

            <div className='flex flex-row gap-2 justify-end items-center'>
              <form method='dialog'>
                <Button color='ghost'>Schließen</Button>
              </form>
              <Button
                color='primary'
                onClick={() => copyToClipboard(exportOutput, 'Dateinamen kopiert')}
                disabled={isLoadingExport || exportFilenames.length === 0}
              >
                <IconCopy size={16} />
                Inhalt kopieren
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

interface SelectionAlbumSectionProps {
  driveId: string
  selectionId: string
  selectionAlbumId: string
  title: string
  filesCount: number
  includeAllFiles: boolean
  onRemoveAlbum: () => void
}

function SelectionAlbumSection({
  driveId,
  selectionId,
  selectionAlbumId,
  title,
  filesCount,
  includeAllFiles,
  onRemoveAlbum,
}: SelectionAlbumSectionProps) {
  const { getSelectionAlbumFiles, removeSelectionAlbumFile } = useClient()
  const queryClient = useQueryClient()
  const removeFileModalRef = useRef<HTMLDialogElement>(null)
  const [fileToRemove, setFileToRemove] = useState<AlbumFile | null>(null)
  const [isRemovingFile, setIsRemovingFile] = useState(false)

  const filesQuery = useQuery({
    queryKey: ['selection', selectionId, 'album', selectionAlbumId, 'files'],
    queryFn: async () => {
      const res = await getSelectionAlbumFiles(selectionId, selectionAlbumId)
      return res.data
    },
  })

  function askRemoveFile(file: AlbumFile) {
    setFileToRemove(file)
    removeFileModalRef.current?.showModal()
  }

  async function confirmRemoveFile() {
    if (!fileToRemove || isRemovingFile) return
    setIsRemovingFile(true)
    try {
      await removeSelectionAlbumFile(driveId, selectionId, selectionAlbumId, fileToRemove.id)
      queryClient.invalidateQueries({ queryKey: ['selection', selectionId, 'album', selectionAlbumId, 'files'] })
      queryClient.invalidateQueries({ queryKey: ['selection', selectionId, 'albums'] })
      removeFileModalRef.current?.close()
      setFileToRemove(null)
    } catch {
      toast.error('Fehler beim Entfernen des Bildes')
    } finally {
      setIsRemovingFile(false)
    }
  }

  const files: AlbumFile[] = filesQuery.data?.results || filesQuery.data || []

  return (
    <div className='bg-neutral p-4 md:p-6 border border-[var(--portal-border)]'>
      <div className='flex flex-row items-center justify-between gap-3 mb-4'>
        <div>
          <h2 className='font-serif text-xl'>{title}</h2>
          <p className='text-xs opacity-60'>
            {filesCount} {filesCount === 1 ? 'Bild' : 'Bilder'}
            {includeAllFiles && ' · Komplettes Album'}
          </p>
        </div>
        <Button color='ghost' size='sm' onClick={onRemoveAlbum} title='Album aus Auswahl entfernen'>
          <IconTrash size={16} />
        </Button>
      </div>
      {filesQuery.isLoading ? (
        <p className='text-sm opacity-60'>Lade Bilder...</p>
      ) : files.length === 0 ? (
        <p className='text-sm opacity-60'>Keine Bilder in diesem Album.</p>
      ) : (
        <div className='grid grid-cols-3 md:grid-cols-5 lg:grid-cols-6 gap-2'>
          {files.map(file => (
            <div key={file.id} className='relative group aspect-square'>
              <img
                src={file.thumbnail || file.detail || file.file}
                alt=''
                className='w-full h-full object-cover rounded-lg'
              />
              <button
                className='absolute top-1 right-1 bg-[black] bg-opacity-60 text-[white] rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity'
                onClick={() => askRemoveFile(file)}
                title='Bild entfernen'
              >
                <IconX size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <Modal ref={removeFileModalRef} className='bg-neutral'>
        <Modal.Header>
          <div className='flex flex-row gap-2 justify-center'>
            <div className='bg-primary text-neutral rounded-full p-1'>
              <IconX size={20} />
            </div>
            Bild aus Auswahl entfernen
          </div>
          <form method='dialog'>
            <button className='btn btn-sm btn-circle btn-ghost absolute right-5 top-5' disabled={isRemovingFile}>✕</button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className='flex flex-col gap-3 items-center'>
            {fileToRemove && (
              <img
                src={fileToRemove.thumbnail || fileToRemove.detail || fileToRemove.file}
                alt=''
                className='w-24 h-24 object-cover rounded-lg'
              />
            )}
            <p className='text-center'>
              Möchtest du dieses Bild aus der Auswahl entfernen?
            </p>
            {fileToRemove?.file_name && (
              <p className='text-center text-xs opacity-60'>{fileToRemove.file_name}</p>
            )}
            <div className='flex flex-row gap-2 mt-2'>
              <form method='dialog'>
                <Button color='ghost' disabled={isRemovingFile}>Abbrechen</Button>
              </form>
              <Button color='error' onClick={confirmRemoveFile} loading={isRemovingFile} disabled={isRemovingFile}>
                Entfernen
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}
